import { useContext } from "react";
import { UseQueryResult, useQuery, useQueries } from "@tanstack/react-query";

import { AxiosContext } from "contexts/AxiosContext";
import {
  Category,
  AnnualKpi,
  AnnualKpiProgress,
  Report,
  SiteKpiProgress,
  Site,
  Target,
  Checklist,
  KpisMachine,
  KpisMachinesWithDates,
  KpisSensorGroup,
  KpiType,
  FleetmonitorKpiAchievements,
  KpiWorkplan,
  ActivityType
} from "interfaces/kpis";
import { makeQueryString } from "core/makeQueryString";
import { PaginatedResponse } from "interfaces/utils";
import { dateFormat } from "core/datetime";
import { dataOrAll } from "hooks/queries/utils/dataOrAll";

import moment from "moment";

const STALE_TIME = 1000 * 60 * 15;

interface CommonArgs {
  ordering?: string;
  page?: number;
  search?: string;
  size?: number;
  enabled?: boolean;
}

interface AnnualKpisQueryArgs extends CommonArgs {
  name?: string;
  name_contains?: string;
  site_id?: string | number;
  site_name?: string;
  site_name_contains?: string;
  year?: string | number;
}

export const useAnnualKpiDetailQuery = ({
  id,
  kpiType,
  activityType,
  enabled
}: {
  id: number;
  kpiType: KpiType;
  activityType: ActivityType;
  enabled: boolean;
}): UseQueryResult<AnnualKpi> => {
  const { axios } = useContext(AxiosContext)!;

  let endpointUrl: string;
  if (kpiType === "field_pro") {
    if (activityType === "maintenance") {
      endpointUrl = `/kpis/maintenance_kpis/${id}`;
    } else if (activityType === "inspection") {
      endpointUrl = `/kpis/fieldpro_inspection_kpis/${id}`;
    }
  }

  if (kpiType === "fleet_monitor") {
    endpointUrl = `/kpis/fleetmonitor_measurement_kpis/${id}`;
  }

  return useQuery({
    queryKey: ["kpis", "detail", id],
    queryFn: async () => {
      const { data } = await axios.get<AnnualKpi>(endpointUrl);
      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisProgressQueryArgs extends CommonArgs {
  category_id?: string;
  category_name?: string;
  category_name_contains?: string;
  id?: string;
  name?: string;
  name_contains?: string;
  year?: string;
}

export const useKpisProgressQuery = (
  args: KpisProgressQueryArgs = {}
): UseQueryResult<PaginatedResponse<AnnualKpiProgress[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "progress", "kpis", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<AnnualKpiProgress[]>>(
        `/kpis/progress/kpis${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

export const useSitesKpisProgressQuery = (
  args: AnnualKpisQueryArgs = {}
): UseQueryResult<PaginatedResponse<SiteKpiProgress[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "progress", "sites_kpis", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<SiteKpiProgress[]>>(
        `/kpis/progress/sites_kpis${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface SitesProgressQueryArgs extends CommonArgs {
  id?: string | number;
  name?: string;
  name_contains?: string;
  year?: string | number;
}

export const useSitesProgressQuery = (
  args: SitesProgressQueryArgs = {}
): UseQueryResult<PaginatedResponse<Site[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "progress", "sites", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Site[]>>(
        `/kpis/progress/sites${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisCategoriesQueryArgs extends CommonArgs {}

export const useKpisCategoriesQuery = (
  args: KpisCategoriesQueryArgs = {}
): UseQueryResult<PaginatedResponse<Category[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "categories", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Category[]>>(
        `/kpis/categories${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisChecklistsQueryArgs extends CommonArgs {}

export const useKpisChecklistsQuery = (
  args: KpisChecklistsQueryArgs = {}
): UseQueryResult<PaginatedResponse<Checklist[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "checklists", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Checklist[]>>(
        `/kpis/read_checklists${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisTargetsQueryArgs extends CommonArgs {
  kpi_id?: string | number;
  site_id?: string | number;
  machine_id?: string | number;
}

export const useKpisTargetsQuery = (
  args: KpisTargetsQueryArgs = {}
): UseQueryResult<PaginatedResponse<Target[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "targets", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Target[]>>(
        `/kpis/targets${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisMachinesWithActivityQueryArgs extends CommonArgs {
  site_id?: string | number;
  site__reference?: string | number;
  checklists?: number[];
  workplans?: number[];
  sensor_groups?: number[];
  is_overdue?: boolean | string;
}

export const useKpisMachinesWithActivityQuery = (
  args: KpisMachinesWithActivityQueryArgs = {},
  activityType: "inspection" | "maintenance" | "fleetmonitor" = "inspection"
): UseQueryResult<PaginatedResponse<KpisMachinesWithDates[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs, true);

  return useQuery({
    queryKey: [
      "kpis",
      "machines",
      `with_${activityType}_activity`,
      dataOrAll(qsArgs)
    ],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<KpisMachine[]>>(
        `kpis/machine_${activityType}_activity${qs}`
      );

      const kpisMachinesWithDates = data.results.map(machine => {
        let latest_activity_human = "—";
        if (machine.latest_activity) {
          latest_activity_human = dateFormat(machine.latest_activity, {
            format: "Do MMM YYYY"
          } as any);
        }

        let overdue_by_human: string | undefined = undefined;

        if (machine.is_overdue) {
          const days_overdue: number = Math.floor(
            moment.duration(machine.overdue_by).asDays()
          );

          switch (days_overdue) {
            case 0:
              overdue_by_human = "Less than 1 day overdue";
              break;
            case 1:
              overdue_by_human = "1 day overdue";
              break;
            default:
              overdue_by_human = `${days_overdue} days overdue`;
          }
        }

        return {
          latest_activity_human,
          latest_activity_sortable: machine.latest_activity ?? "0",
          overdue_by_human,
          ...machine
        };
      });

      return { results: kpisMachinesWithDates };
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisSensorGroupsQueryArgs extends CommonArgs {
  kpi_id?: string | number;
  machine_id?: string | number;
}

export const useKpisSensorGroupsQuery = (
  args: KpisSensorGroupsQueryArgs = {}
): UseQueryResult<PaginatedResponse<KpisSensorGroup[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQuery({
    queryKey: ["kpis", "sensor_groups", dataOrAll(qsArgs)],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<KpisSensorGroup[]>>(
        `/kpis/fleetmonitor_sensor_groups${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

interface KpisReportsQueryArgs extends CommonArgs {
  kpi_id?: string | number;
  machine_id?: string | number;
}

export const useKpisTargetsAndReportsQueries = (
  args: KpisTargetsQueryArgs & KpisReportsQueryArgs = {}
): [
  UseQueryResult<PaginatedResponse<Target[]>, unknown>,
  UseQueryResult<PaginatedResponse<Report[]>, unknown>,
  UseQueryResult<PaginatedResponse<FleetmonitorKpiAchievements[]>, unknown>
] => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs);

  return useQueries({
    queries: [
      {
        queryKey: ["kpis", "targets", qsArgs],
        queryFn: async () => {
          const { data } = await axios.get<PaginatedResponse<Target[]>>(
            `/kpis/targets${qs}`
          );

          return data;
        },
        staleTime: STALE_TIME,
        enabled
      },
      {
        queryKey: ["kpis", "reports", qsArgs],
        queryFn: async () => {
          const { data } = await axios.get<PaginatedResponse<Report[]>>(
            `/kpis/reports${qs}`
          );

          return data;
        },
        staleTime: STALE_TIME,
        enabled
      },
      {
        queryKey: ["kpis", "fm_measurements", qsArgs],
        queryFn: async () => {
          const { data } = await axios.get<
            PaginatedResponse<FleetmonitorKpiAchievements[]>
          >(`/kpis/fleetmonitor_measurement_kpi_achievements${qs}`);

          return data;
        },
        staleTime: STALE_TIME,
        enabled
      }
    ]
  });
};

interface KpisWorkplansQueryArgs extends CommonArgs {
  checklist?: number[];
}

export const useKpiWorkplansQuery = (
  args: KpisWorkplansQueryArgs
): UseQueryResult<PaginatedResponse<KpiWorkplan[]>, unknown> => {
  const { axios } = useContext(AxiosContext)!;

  const { enabled: isEnabled, ...qsArgs } = args;
  const enabled = isEnabled ?? true;

  const qs = makeQueryString(qsArgs, true);

  return useQuery({
    queryKey: ["kpis", "workplans", qsArgs],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Report[]>>(
        `/kpis/read_workplans${qs}`
      );

      return data;
    },
    staleTime: STALE_TIME,
    enabled
  });
};

export const useKpiYearsUniqueQuery = (
  enabled = true
): UseQueryResult<number[], unknown> => {
  const { axios } = useContext(AxiosContext)!;

  return useQuery({
    queryKey: ["kpis", "years"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<Site[]>>(
        `/kpis/progress/kpis?page=1&size=999999&ordering=-year`
      );

      const years = data.results.map(item => String(item.year));
      const uniqueYears = [...new Set(years)];

      return uniqueYears;
    },
    staleTime: STALE_TIME,
    enabled
  });
};
